import React, { useMemo, useState } from 'react';
import { resolveRate, calculateAmount, formatCurrency, formatDecimalHours } from './timeTrackerUtils';
import { generateInvoicePDF, type InvoiceLineItem } from '@/utils/invoicePdfGenerator';
import type { TimeTrackerEntry, TimeTrackerProject, TimeTrackerClient, TimeTrackerTask, TimeTrackerInvoiceConfig } from '@/types/pm-types';
import { pmTheme } from './pmTheme';

interface TimeTrackerInvoiceViewProps {
  entries: TimeTrackerEntry[];
  projects: TimeTrackerProject[];
  clients: TimeTrackerClient[];
  tasks: TimeTrackerTask[];
  currency?: string;
}

export const TimeTrackerInvoiceView: React.FC<TimeTrackerInvoiceViewProps> = ({
  entries,
  projects,
  clients,
  tasks,
  currency = 'USD',
}) => {
  const [selectedClientId, setSelectedClientId] = useState(clients[0]?.id || '');
  const [selectedProjectIds, setSelectedProjectIds] = useState<string[]>([]);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [invoiceNumber, setInvoiceNumber] = useState('');
  const [purchaseOrder, setPurchaseOrder] = useState('');
  const [taxRate, setTaxRate] = useState(0);
  const [notes, setNotes] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [companyAddress, setCompanyAddress] = useState('');

  // Filter entries for invoice
  const invoiceEntries = useMemo(() => {
    let filtered = entries.filter((e) => e.billable);

    if (selectedClientId) {
      const clientProjectIds = projects.filter((p) => p.clientId === selectedClientId).map((p) => p.id);
      filtered = filtered.filter((e) => e.projectId && clientProjectIds.includes(e.projectId));
    }

    if (selectedProjectIds.length > 0) {
      filtered = filtered.filter((e) => e.projectId && selectedProjectIds.includes(e.projectId));
    }

    if (startDate) filtered = filtered.filter((e) => e.date >= startDate);
    if (endDate) filtered = filtered.filter((e) => e.date <= endDate);

    return filtered;
  }, [entries, selectedClientId, selectedProjectIds, startDate, endDate, projects]);

  // Build line items grouped by project — accumulate amounts per-entry
  // to handle tasks with different rates under the same project
  const lineItems: InvoiceLineItem[] = useMemo(() => {
    const byProject = new Map<string, { hours: number; amount: number }>();

    for (const entry of invoiceEntries) {
      const projectId = entry.projectId || 'unknown';
      const rate = resolveRate(projects, tasks, entry.projectId, entry.taskId);
      const hours = entry.duration / 3600;
      const existing = byProject.get(projectId);
      if (existing) {
        existing.hours += hours;
        existing.amount += hours * rate;
      } else {
        byProject.set(projectId, { hours, amount: hours * rate });
      }
    }

    return [...byProject.entries()].map(([projectId, data]) => {
      const project = projects.find((p) => p.id === projectId);
      const effectiveRate = data.hours > 0 ? data.amount / data.hours : 0;
      return {
        description: project?.name || 'Project Work',
        hours: Math.round(data.hours * 100) / 100,
        rate: Math.round(effectiveRate * 100) / 100,
        amount: Math.round(data.amount * 100) / 100,
      };
    });
  }, [invoiceEntries, projects, tasks]);

  const subtotal = useMemo(() => lineItems.reduce((sum, item) => sum + item.amount, 0), [lineItems]);
  const tax = useMemo(() => subtotal * (taxRate / 100), [subtotal, taxRate]);
  const total = subtotal + tax;

  const selectedClient = clients.find((c) => c.id === selectedClientId);

  const handleDownloadPDF = () => {
    if (!selectedClient) return;

    const config: TimeTrackerInvoiceConfig = {
      clientId: selectedClientId,
      projectIds: selectedProjectIds.length > 0 ? selectedProjectIds : undefined,
      dateRange: { start: startDate || 'N/A', end: endDate || 'N/A' },
      invoiceNumber: invoiceNumber || undefined,
      purchaseOrder: purchaseOrder || undefined,
      taxRate: taxRate || undefined,
      notes: notes || undefined,
      companyName: companyName || undefined,
      companyAddress: companyAddress || undefined,
    };

    generateInvoicePDF({
      invoiceConfig: config,
      lineItems,
      client: selectedClient,
      subtotal,
      tax,
      total,
    });
  };

  const clientProjects = useMemo(
    () => projects.filter((p) => !selectedClientId || p.clientId === selectedClientId),
    [projects, selectedClientId]
  );

  const inputStyle: React.CSSProperties = {
    width: '100%',
    padding: '6px 8px',
    border: `1px solid ${pmTheme.input}`,
    borderRadius: 6,
    fontSize: 13,
    color: pmTheme.text,
    background: pmTheme.surface,
  };

  return (
    <div style={{ display: 'flex', height: '100%', overflow: 'hidden' }}>
      {/* Configuration panel */}
      <div style={{ width: 300, borderRight: `1px solid ${pmTheme.border}`, padding: 16, overflow: 'auto', background: pmTheme.surfaceSubtle }}>
        <h3 style={{ fontSize: 14, fontWeight: 700, color: pmTheme.text, marginBottom: 16 }}>Invoice Settings</h3>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div>
            <label style={{ fontSize: 12, color: pmTheme.textMuted, fontWeight: 600, display: 'block', marginBottom: 4 }}>Client</label>
            <select
              value={selectedClientId}
              onChange={(e) => setSelectedClientId(e.target.value)}
              style={inputStyle}
            >
              <option value="">Select client...</option>
              {clients.map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          </div>

          <div>
            <label style={{ fontSize: 12, color: pmTheme.textMuted, fontWeight: 600, display: 'block', marginBottom: 4 }}>Projects (optional)</label>
            <select
              multiple
              value={selectedProjectIds}
              onChange={(e) => setSelectedProjectIds([...e.target.selectedOptions].map((o) => o.value))}
              style={{ ...inputStyle, padding: '4px', minHeight: 60 }}
            >
              {clientProjects.map((p) => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </select>
          </div>

          <div style={{ display: 'flex', gap: 8 }}>
            <div style={{ flex: 1 }}>
              <label style={{ fontSize: 12, color: pmTheme.textMuted, fontWeight: 600, display: 'block', marginBottom: 4 }}>Start Date</label>
              <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} style={inputStyle} />
            </div>
            <div style={{ flex: 1 }}>
              <label style={{ fontSize: 12, color: pmTheme.textMuted, fontWeight: 600, display: 'block', marginBottom: 4 }}>End Date</label>
              <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} style={inputStyle} />
            </div>
          </div>

          <div>
            <label style={{ fontSize: 12, color: pmTheme.textMuted, fontWeight: 600, display: 'block', marginBottom: 4 }}>Invoice Number</label>
            <input type="text" value={invoiceNumber} onChange={(e) => setInvoiceNumber(e.target.value)} placeholder="INV-001" style={inputStyle} />
          </div>

          <div>
            <label style={{ fontSize: 12, color: pmTheme.textMuted, fontWeight: 600, display: 'block', marginBottom: 4 }}>PO Number</label>
            <input type="text" value={purchaseOrder} onChange={(e) => setPurchaseOrder(e.target.value)} placeholder="Optional" style={inputStyle} />
          </div>

          <div>
            <label style={{ fontSize: 12, color: pmTheme.textMuted, fontWeight: 600, display: 'block', marginBottom: 4 }}>Tax Rate (%)</label>
            <input type="number" value={taxRate} onChange={(e) => setTaxRate(Number(e.target.value))} min={0} max={100} step={0.5} style={inputStyle} />
          </div>

          <div>
            <label style={{ fontSize: 12, color: pmTheme.textMuted, fontWeight: 600, display: 'block', marginBottom: 4 }}>Company Name</label>
            <input type="text" value={companyName} onChange={(e) => setCompanyName(e.target.value)} placeholder="Your Company" style={inputStyle} />
          </div>

          <div>
            <label style={{ fontSize: 12, color: pmTheme.textMuted, fontWeight: 600, display: 'block', marginBottom: 4 }}>Company Address</label>
            <textarea value={companyAddress} onChange={(e) => setCompanyAddress(e.target.value)} rows={2} style={{ ...inputStyle, resize: 'vertical' }} />
          </div>

          <div>
            <label style={{ fontSize: 12, color: pmTheme.textMuted, fontWeight: 600, display: 'block', marginBottom: 4 }}>Notes</label>
            <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} placeholder="Payment terms, thank you message..." style={{ ...inputStyle, resize: 'vertical' }} />
          </div>

          <button
            onClick={handleDownloadPDF}
            disabled={!selectedClient}
            style={{
              padding: '10px 16px',
              background: selectedClient ? '#10b981' : pmTheme.input,
              color: pmTheme.textInverse,
              border: 'none',
              borderRadius: 8,
              fontSize: 14,
              fontWeight: 600,
              cursor: selectedClient ? 'pointer' : 'not-allowed',
              marginTop: 8,
            }}
          >
            Download PDF
          </button>
        </div>
      </div>

      {/* Preview panel */}
      <div style={{ flex: 1, overflow: 'auto', padding: 24 }}>
        <div style={{ maxWidth: 600, margin: '0 auto', background: pmTheme.surface, border: `1px solid ${pmTheme.border}`, borderRadius: 8, padding: 32, boxShadow: pmTheme.shadow }}>
          {/* Invoice header */}
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 24 }}>
            <div>
              <h2 style={{ fontSize: 24, fontWeight: 700, color: pmTheme.text, margin: 0 }}>INVOICE</h2>
              {companyName && <div style={{ fontSize: 14, color: pmTheme.text, marginTop: 4, fontWeight: 600 }}>{companyName}</div>}
              {companyAddress && <div style={{ fontSize: 12, color: pmTheme.textMuted, marginTop: 2, whiteSpace: 'pre-line' }}>{companyAddress}</div>}
            </div>
            <div style={{ textAlign: 'right' }}>
              {invoiceNumber && <div style={{ fontSize: 14, color: pmTheme.text, fontWeight: 600 }}>#{invoiceNumber}</div>}
              <div style={{ fontSize: 12, color: pmTheme.textMuted, marginTop: 2 }}>{new Date().toLocaleDateString()}</div>
              {startDate && endDate && (
                <div style={{ fontSize: 11, color: pmTheme.textSubtle, marginTop: 2 }}>{startDate} — {endDate}</div>
              )}
            </div>
          </div>

          {/* Bill to */}
          {selectedClient && (
            <div style={{ marginBottom: 24, padding: 12, background: pmTheme.surfaceSubtle, borderRadius: 6 }}>
              <div style={{ fontSize: 11, color: pmTheme.textMuted, fontWeight: 600, marginBottom: 4 }}>BILL TO</div>
              <div style={{ fontSize: 14, color: pmTheme.text, fontWeight: 600 }}>{selectedClient.name}</div>
              {selectedClient.contactEmail && <div style={{ fontSize: 12, color: pmTheme.textMuted }}>{selectedClient.contactEmail}</div>}
              {selectedClient.address && <div style={{ fontSize: 12, color: pmTheme.textMuted, whiteSpace: 'pre-line' }}>{selectedClient.address}</div>}
            </div>
          )}

          {/* Line items */}
          <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 16 }}>
            <thead>
              <tr style={{ borderBottom: `2px solid ${pmTheme.border}` }}>
                <th style={{ padding: '8px 0', textAlign: 'left', fontSize: 11, color: pmTheme.textMuted, fontWeight: 600 }}>Description</th>
                <th style={{ padding: '8px 0', textAlign: 'right', fontSize: 11, color: pmTheme.textMuted, fontWeight: 600 }}>Hours</th>
                <th style={{ padding: '8px 0', textAlign: 'right', fontSize: 11, color: pmTheme.textMuted, fontWeight: 600 }}>Rate</th>
                <th style={{ padding: '8px 0', textAlign: 'right', fontSize: 11, color: pmTheme.textMuted, fontWeight: 600 }}>Amount</th>
              </tr>
            </thead>
            <tbody>
              {lineItems.map((item, i) => (
                <tr key={i} style={{ borderBottom: `1px solid ${pmTheme.borderSubtle}` }}>
                  <td style={{ padding: '10px 0', fontSize: 13, color: pmTheme.text }}>{item.description}</td>
                  <td style={{ padding: '10px 0', fontSize: 13, color: pmTheme.textMuted, textAlign: 'right' }}>{item.hours.toFixed(1)}</td>
                  <td style={{ padding: '10px 0', fontSize: 13, color: pmTheme.textMuted, textAlign: 'right' }}>{formatCurrency(item.rate, currency)}</td>
                  <td style={{ padding: '10px 0', fontSize: 13, color: pmTheme.text, textAlign: 'right', fontWeight: 600 }}>{formatCurrency(item.amount, currency)}</td>
                </tr>
              ))}
              {lineItems.length === 0 && (
                <tr>
                  <td colSpan={4} style={{ padding: 16, textAlign: 'center', color: pmTheme.textSubtle, fontSize: 13 }}>
                    No billable entries found for selected filters.
                  </td>
                </tr>
              )}
            </tbody>
          </table>

          {/* Totals */}
          <div style={{ borderTop: `2px solid ${pmTheme.border}`, paddingTop: 12 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
              <span style={{ fontSize: 13, color: pmTheme.textMuted }}>Subtotal</span>
              <span style={{ fontSize: 13, color: pmTheme.text }}>{formatCurrency(subtotal, currency)}</span>
            </div>
            {taxRate > 0 && (
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                <span style={{ fontSize: 13, color: pmTheme.textMuted }}>Tax ({taxRate}%)</span>
                <span style={{ fontSize: 13, color: pmTheme.text }}>{formatCurrency(tax, currency)}</span>
              </div>
            )}
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 8, paddingTop: 8, borderTop: `1px solid ${pmTheme.border}` }}>
              <span style={{ fontSize: 16, fontWeight: 700, color: pmTheme.text }}>Total</span>
              <span style={{ fontSize: 16, fontWeight: 700, color: pmTheme.text }}>{formatCurrency(total, currency)}</span>
            </div>
          </div>

          {/* Notes */}
          {notes && (
            <div style={{ marginTop: 24, padding: 12, background: pmTheme.surfaceSubtle, borderRadius: 6 }}>
              <div style={{ fontSize: 11, color: pmTheme.textMuted, fontWeight: 600, marginBottom: 4 }}>NOTES</div>
              <div style={{ fontSize: 12, color: pmTheme.text, whiteSpace: 'pre-line' }}>{notes}</div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

TimeTrackerInvoiceView.displayName = 'TimeTrackerInvoiceView';
